import { useCallback, useEffect, useRef, useState } from "react";
import { confirmable, createConfirmation } from "react-confirm";

// Dark, Summer-League-styled confirm dialog (replaces the stock light Bootstrap modal). Used by
// every confirm() call app-wide — e.g. "Start Free Agency" while players are unsigned, delete
// prompts, rename prompts (defaultValue supplies a text input).
const Confirm = confirmable<
	{
		confirmation: string;
		defaultValue?: string;
		okText?: string;
		cancelText?: string;
	},
	boolean | string | null
>(({ show, proceed, confirmation, defaultValue, okText, cancelText }) => {
	okText = okText ?? "OK";
	cancelText = cancelText ?? "Cancel";
	const [controlledValue, setControlledValue] = useState(defaultValue ?? "");
	const ok = useCallback(
		() => proceed(defaultValue === undefined ? true : controlledValue),
		[controlledValue, defaultValue, proceed],
	);
	const cancel = useCallback(
		() => proceed(defaultValue === undefined ? false : null),
		[defaultValue, proceed],
	);
	const inputRef = useRef<HTMLInputElement>(null);
	const okRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.select();
		} else if (okRef.current) {
			okRef.current.focus();
		}
	}, []);

	if (!show) {
		return null;
	}

	return (
		<div className="slc-backdrop" onClick={cancel}>
			<style>{styles}</style>
			<div
				className="slc-card"
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
			>
				<div className="slc-msg">{confirmation}</div>
				{defaultValue !== undefined ? (
					<form
						onSubmit={(event) => {
							event.preventDefault();
							ok();
						}}
					>
						<input
							ref={inputRef}
							type="text"
							className="slc-input"
							value={controlledValue}
							onChange={(event) => {
								setControlledValue(event.target.value);
							}}
						/>
					</form>
				) : null}
				<button className="slc-ok" onClick={ok} ref={okRef}>
					{okText}
				</button>
				<button className="slc-cancel" onClick={cancel}>
					{cancelText}
				</button>
			</div>
		</div>
	);
});

const styles = `
.slc-backdrop { position:fixed; inset:0; z-index:3500; background:rgba(0,0,0,.6);
  display:flex; align-items:flex-end; justify-content:center;
  font-family:Montserrat, system-ui, sans-serif; animation:slcFade .18s ease; }
.slc-card { width:100%; max-width:520px; background:#1c1c1c; color:#fff;
  border-radius:22px 22px 0 0; padding:24px 20px max(22px, env(safe-area-inset-bottom));
  box-shadow:0 -20px 50px rgba(0,0,0,.5); animation:slcUp .26s cubic-bezier(.2,.9,.3,1); }
.slc-msg { font-size:14.5px; font-weight:600; line-height:1.5; color:#e6e6e6; margin-bottom:20px; }
.slc-msg b { color:#fff; font-weight:800; }
.slc-input { width:100%; background:#121212; border:1px solid #2a2a2a; border-radius:12px;
  color:#fff; font-family:inherit; font-size:15px; font-weight:600; padding:12px 14px;
  margin-bottom:18px; outline:none; }
.slc-input:focus { border-color:#18E0A8; }
.slc-ok { width:100%; border:none; border-radius:26px; background:#18E0A8; color:#0a0a0a;
  font-family:inherit; font-weight:800; font-size:15px; padding:14px; cursor:pointer;
  box-shadow:0 10px 26px rgba(24,224,168,.28); }
.slc-cancel { width:100%; border:none; background:transparent; color:#cdd4da;
  font-family:inherit; font-weight:700; font-size:14px; padding:13px 12px 2px; cursor:pointer; }
@keyframes slcFade { from { opacity:0 } to { opacity:1 } }
@keyframes slcUp { from { transform:translateY(100%) } to { transform:none } }
@media (min-width:640px) {
  .slc-backdrop { align-items:center; }
  .slc-card { border-radius:20px; }
}
`;

const confirmFunction = createConfirmation(Confirm);

export function confirm(
	message: string,
	options: {
		defaultValue: string;
		okText?: string;
		cancelText?: string;
	},
): Promise<string | null>;

export function confirm(
	message: string,
	options?: {
		defaultValue?: undefined;
		okText?: string;
		cancelText?: string;
	},
): Promise<boolean>;

export function confirm(
	message: string,
	{
		defaultValue,
		okText,
		cancelText,
	}: {
		defaultValue?: string;
		okText?: string;
		cancelText?: string;
	} = {},
) {
	return confirmFunction({
		confirmation: message,
		defaultValue,
		okText,
		cancelText,
	});
}
